/**
 * IndexedDB 消息存储
 */

import { openDB } from 'idb';
import type { Message, StoredMessages } from '@/types';

const DB_NAME = 'sse-ai-chat';
const DB_VERSION = 1;
const MESSAGES_STORE = 'messages';
const MAX_MESSAGES_PER_SERVER = 100; // 每个服务器最多存储 100 条
/** 与 Node Agent 会话 TTL 对齐：超过则清空本地记录，避免重装后仍显示陈旧对话 */
const MESSAGE_RETENTION_MS = 24 * 60 * 60 * 1000;

// 初始化数据库
const initDB = async () => {
  return openDB(DB_NAME, DB_VERSION, {
    upgrade(db) {
      // 消息存储
      if (!db.objectStoreNames.contains(MESSAGES_STORE)) {
        const store = db.createObjectStore(MESSAGES_STORE, { keyPath: 'serverId' });
        store.createIndex('updatedAt', 'updatedAt');
      }
    },
  });
};

// 保存消息
export const saveMessages = async (serverId: number, messages: Message[]): Promise<void> => {
  try {
    const db = await initDB();
    
    // 限制消息数量，保留最新的
    const limitedMessages = messages.slice(-MAX_MESSAGES_PER_SERVER);
    
    const data: StoredMessages = {
      serverId,
      messages: limitedMessages,
      updatedAt: Date.now(),
    };
    
    await db.put(MESSAGES_STORE, data);
  } catch (error) {
    console.error('[Storage] Save messages failed:', error);
  }
};

// 加载消息
export const loadMessages = async (serverId: number): Promise<Message[]> => {
  try {
    const db = await initDB();
    const data = await db.get(MESSAGES_STORE, serverId);
    if (!data?.messages?.length) {
      return [];
    }
    const age = Date.now() - (data.updatedAt || 0);
    if (age > MESSAGE_RETENTION_MS) {
      await db.delete(MESSAGES_STORE, serverId);
      return [];
    }
    return data.messages;
  } catch (error) {
    console.error('[Storage] Load messages failed:', error);
    return [];
  }
};

// 删除服务器消息
export const deleteMessages = async (serverId: number): Promise<void> => {
  try {
    const db = await initDB();
    await db.delete(MESSAGES_STORE, serverId);
  } catch (error) {
    console.error('[Storage] Delete messages failed:', error);
  }
};

// 清空所有消息
export const clearAllMessages = async (): Promise<void> => {
  try {
    const db = await initDB();
    await db.clear(MESSAGES_STORE);
  } catch (error) {
    console.error('[Storage] Clear all messages failed:', error);
  }
};

// 获取所有服务器的历史
export const getAllStoredServers = async (): Promise<StoredMessages[]> => {
  try {
    const db = await initDB();
    const all = await db.getAll(MESSAGES_STORE);
    const now = Date.now();
    const fresh: StoredMessages[] = [];
    for (const row of all) {
      if (now - (row.updatedAt || 0) > MESSAGE_RETENTION_MS) {
        await db.delete(MESSAGES_STORE, row.serverId);
        continue;
      }
      fresh.push(row);
    }
    return fresh;
  } catch (error) {
    console.error('[Storage] Get all stored servers failed:', error);
    return [];
  }
};